import React, { useState, useContext, useEffect, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Input from 'components/Input'
import Textarea from 'components/Textarea'
import Checkbox from 'components/Checkbox'
import Tooltip from 'components/Tooltip'
import MultiSelectionDropdown from 'components/multiSelectDropdown'
import ecoSystem from 'constants/ecoSystem'
import InfoIcon from 'assets/icons/info-icon'
import { CountriesContext } from 'providers/countries'
import { SiteContext } from 'providers/site'
import { MySiteContext } from 'providers/mySiteProvider'
import { delay, delayCancellable } from 'utils/delay'
import ScrollToTop from 'components/ScrollToTop'
import AssessmentTitle from 'components/AssessmentTitle'
import Habitats from './habitats/components/habitats'
import { ProfileContext } from 'providers/profile'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { useSiteEditPermissions } from '../../hooks/useSiteEditPermissions'
import { useBoundaryGeometry } from '../../hooks/useBoundaryGeometry'
import { useDelayedSiteUpdate } from '../../hooks/useDelayedSiteUpdate'
import { buildDetailsInitialState } from './utils/detailsInitialState'
import { AssessmentPageSubTittleIconWrapper } from '../assessment/style'
import {
  handleCalculatedCoverageChange,
  handleRangeFieldChange,
  hasAlternativeCoverageValue,
  isNumericInputAllowed,
  hasProtectedOrConservedCoverage
} from './utils/numericFieldHandlers'

const minMaxFields = {
  min: 'minimumDepth',
  max: 'maximumDepth'
}

const minMaxAltFields = {
  min: 'lowestAltitude',
  max: 'highestAltitude'
}

const inputSaveDelayTime = 300
const updateSiteNameTimeout = delay(inputSaveDelayTime)
const updateSiteNameNationalTimeout = delay(inputSaveDelayTime)
const updateStateChange = delay(inputSaveDelayTime)
const updateSiteAreaTimeout = delay(inputSaveDelayTime)
const updateLatitudeOfMidPointTimeout = delay(inputSaveDelayTime)
const updateLongitudeOfMidPointTimeout = delay(inputSaveDelayTime)
const updateRationaleForSiteInformationTimeout = delay(inputSaveDelayTime)
const updateSiteDescriptionTimeout = delay(inputSaveDelayTime)
const sensitiveSiteDetailsDebounce = delayCancellable(inputSaveDelayTime)
const updateHowIsTheSiteManagedTimeout = delay(inputSaveDelayTime)
const updateDeliniationRationaleTimeout = delay(inputSaveDelayTime)
const updateBoundarySourceTimeout = delay(inputSaveDelayTime)
const updateBiodiversityValuesTimeout = delay(inputSaveDelayTime)
const updateLandUseRegimesAtSiteTimeout = delay(inputSaveDelayTime)
const updateNotesTimeout = delay(inputSaveDelayTime)
const updateIndigenousGroupsTimeout = delay(inputSaveDelayTime)
const updateThreatsDescriptionValuesTimeout = delay(inputSaveDelayTime)
const updateMaximumDepthValuesTimeout = delay(inputSaveDelayTime)
const updateSupersededValuesTimeout = delay(inputSaveDelayTime)
const updateCoveredByPaValuesTimeout = delay(inputSaveDelayTime)
const updateCoveredByOECMValuesTimeout = delay(inputSaveDelayTime)
const updateCoveredByWhatProtectedAreasTimeout = delay(inputSaveDelayTime)
const updatePaUserUpdatedTimeout = delay(inputSaveDelayTime)
const updatePaUserUpdatedJustificationTimeout = delay(inputSaveDelayTime)
const updateOecmUserUpdatedTimeout = delay(inputSaveDelayTime)
const updateOecmUserUpdatedJustificationTimeout = delay(inputSaveDelayTime)
const updateHabitatTimeout = delay(inputSaveDelayTime)
const updateReferencessTimeout = delay(inputSaveDelayTime)
const contributorsSavetimeout = delay(inputSaveDelayTime)
const tooltipXOffset = 0
const tooltipYOffset = 16

const getRequiredFieldError = (isValid, field) => {
  if (isValid) {
    return { field: '', error: '' }
  }

  return {
    field,
    error: 'pleaseCompleteThisField'
  }
}

const getAltitudeFieldError = (isValid, altitudeError, field) => {
  if (!isValid) {
    return getRequiredFieldError(false, field)
  }

  if (altitudeError.field === field) {
    return altitudeError
  }

  return { field: '', error: '' }
}

const getDepthFieldError = (depthError, field) => {
  if (depthError.field === field) {
    return depthError
  }

  return { field: '', error: '' }
}

const DetailsPage = () => {
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const { refreshSiteCounts, refreshSites } = useContext(MySiteContext)
  const { countriesOptions } = useContext(CountriesContext)
  const params = useParams()
  const siteData = site || {}

  const { disabled, editOnlySiteStatus } = useSiteEditPermissions({
    site: siteData,
    isEditable,
    roleId: profile?.RoleId
  })

  const canEditPaOecmOverrides =
    editOnlySiteStatus ||
    (siteData.SiteStatusId === SITE_STATUS_DRAFT_ID && !disabled)

  const historyId = editOnlySiteStatus ? siteData.historyId : params.siteHistoryId
  const isReassessment = Boolean(params.siteHistoryId)

  const { saveSiteData } = useDelayedSiteUpdate({ site: siteData, params, historyId })

  useBoundaryGeometry({ site: siteData, params, historyId, isReassessment })

  const initialState = buildDetailsInitialState(siteData)

  const [name, setName] = useState(initialState.name)
  const [siteNameNational, setSiteNameNational] = useState(initialState.siteNameNational)
  const [CountryCode, setCountryCode] = useState(initialState.countryCode)
  const [state, setState] = useState(initialState.state)
  const [siteArea, setSiteArea] = useState(initialState.siteArea)
  const [latitudeOfMidPoint, setLatitudeOfMidPoint] = useState(initialState.latitudeOfMidPoint)
  const [longitudeOfMidPoint, setLongitudeOfMidPoint] = useState(initialState.longitudeOfMidPoint)
  const [highestAltitude, setHighestAltitude] = useState(initialState.highestAltitude)
  const [lowestAltitude, setLowestAltitude] = useState(initialState.lowestAltitude)
  const [minimumDepth, setMinimumDepth] = useState(initialState.minimumDepth)
  const [maximumDepth, setMaximumDepth] = useState(initialState.maximumDepth)
  const [depthError, setDepthError] = useState({ field: '', error: '' })
  const [altitudeError, setAltitudeError] = useState({ field: '', error: '' })
  const [selectedSystems, setSelectedSystems] = useState(initialState.selectedSystems)
  const [rationaleForSiteInformation, setRationaleForSiteInformation] = useState(initialState.rationaleForSiteInformation)
  const [siteDescription, setSiteDescription] = useState(initialState.siteDescription)
  const [sensitiveSite, setSensitiveSite] = useState(initialState.sensitiveSite)
  const [sensitiveSiteDetails, setSensitiveSiteDetails] = useState(initialState.sensitiveSiteDetails)
  const sensitiveSiteRef = useRef(sensitiveSite)
  sensitiveSiteRef.current = sensitiveSite
  const [howIsTheSiteManaged, setHowIsTheSiteManaged] = useState(initialState.howIsTheSiteManaged)
  const [deliniationRationale, setDeliniationRationale] = useState(initialState.deliniationRationale)
  const [boundarySource, setBoundarySource] = useState(initialState.boundarySource)
  const [additionalBiodiversityValues, setAdditionalBiodiversityValues] = useState(initialState.additionalBiodiversityValues)
  const [landUseRegimesAtSite, setLandUseRegimesAtSite] = useState(initialState.landUseRegimesAtSite)
  const [coveredByWhatProtectedAreas, setCoveredByWhatProtectedAreas] = useState(initialState.coveredByWhatProtectedAreas)
  const [coveredByPa, setCoveredByPa] = useState(initialState.coveredByPa)
  const [coveredByOECM, setCoveredByOECM] = useState(initialState.coveredByOECM)
  const [paUserUpdated, setPaUserUpdated] = useState(initialState.paUserUpdated)
  const [paUserUpdatedJustification, setPaUserUpdatedJustification] = useState(initialState.paUserUpdatedJustification)
  const [oecmUserUpdated, setOecmUserUpdated] = useState(initialState.oecmUserUpdated)
  const [oecmUserUpdatedJustification, setOecmUserUpdatedJustification] = useState(initialState.oecmUserUpdatedJustification)
  const [threatsDescription, setThreatsDescription] = useState(initialState.threatsDescription)
  const [supersidedSites, setSupersidedSites] = useState(initialState.supersidedSites)
  const [notes, setNotes] = useState(initialState.notes)
  const [habitat, setHabitat] = useState(initialState.habitat)
  const [indigenousGroups, setIndigenousGroups] = useState(initialState.indigenousGroups)
  const [references, setReferences] = useState(initialState.references)
  const [contributors, setContributors] = useState(initialState.contributors)
  const [countryInput, setCountryInput] = useState('')

  // Re-sync fields that the Map page can update while this page is mounted
  useEffect(() => {
    setSiteArea(siteData.siteArea)
    setLatitudeOfMidPoint(siteData.latitudeOfMidPoint)
    setLongitudeOfMidPoint(siteData.longitudeOfMidPoint)
  }, [siteData])

  if (!siteData.id) {
    return null
  }

  const handleSearchCountry = (e) => {
    setCountryInput(e)
  }

  const handleNameChange = async (e) => {
    setName(e.target.value)
    await updateSiteNameTimeout(() => saveSiteData({ name: e.target.value }))
  }

  const handleIndigenousGroupsChange = async (e) => {
    setIndigenousGroups(e.target.value)
    await updateIndigenousGroupsTimeout(() => saveSiteData({ indigenousGroups: e.target.value }))
  }

  const handleSiteNationalNameChange = async (e) => {
    setSiteNameNational(e.target.value)
    await updateSiteNameNationalTimeout(() => saveSiteData({ siteNameNational: e.target.value }))
  }

  const handleCountryChange = async (e) => {
    setCountryCode(e)
    await saveSiteData({ SiteDetailsCountryId: e })
  }

  const handleStateChange = async (e) => {
    setState(e.target.value)
    await updateStateChange(() => saveSiteData({ state: e.target.value }))
  }

  const handleAreaChange = async (e) => {
    if (isNumericInputAllowed(e.target.value)) {
      setSiteArea(e.target.value)
    }
    await updateSiteAreaTimeout(() =>
      saveSiteData({ siteArea: e.target.value === '' ? null : Number(e.target.value).toFixed(10) })
    )
  }

  const handleLatitudeChange = async (e) => {
    if (isNumericInputAllowed(e.target.value)) {
      setLatitudeOfMidPoint(e.target.value)
    }
    await updateLatitudeOfMidPointTimeout(() =>
      saveSiteData({ latitudeOfMidPoint: e.target.value === '' ? null : Number(e.target.value).toFixed(10) })
    )
  }

  const handleLongitudeChange = async (e) => {
    if (isNumericInputAllowed(e.target.value)) {
      setLongitudeOfMidPoint(e.target.value)
    }
    await updateLongitudeOfMidPointTimeout(() =>
      saveSiteData({ longitudeOfMidPoint: e.target.value === '' ? null : Number(e.target.value).toFixed(10) })
    )
  }

  const handleHighestAltitudeChange = async (e) => {
    await handleRangeFieldChange({
      value: e.target.value,
      firstValue: lowestAltitude,
      secondValue: e.target.value,
      validationFields: minMaxAltFields,
      validationBoundary: 'max',
      setValue: setHighestAltitude,
      setError: setAltitudeError,
      saveSiteData,
      saveFieldName: 'highestAltitude',
      requireNoZeroOnValidRange: false
    })
  }

  const handleLowestAltitudeChange = async (e) => {
    await handleRangeFieldChange({
      value: e.target.value,
      firstValue: e.target.value,
      secondValue: highestAltitude,
      validationFields: minMaxAltFields,
      validationBoundary: 'min',
      setValue: setLowestAltitude,
      setError: setAltitudeError,
      saveSiteData,
      saveFieldName: 'lowestAltitude'
    })
  }

  const handleMinimumDepthChange = async (e) => {
    await handleRangeFieldChange({
      value: e.target.value,
      firstValue: e.target.value,
      secondValue: maximumDepth,
      validationFields: minMaxFields,
      validationBoundary: 'min',
      setValue: setMinimumDepth,
      setError: setDepthError,
      saveSiteData,
      saveFieldName: 'minDepth'
    })
  }

  const handleMaximumDepthChange = async (e) => {
    await handleRangeFieldChange({
      value: e.target.value,
      firstValue: minimumDepth,
      secondValue: e.target.value,
      validationFields: minMaxFields,
      validationBoundary: 'max',
      setValue: setMaximumDepth,
      setError: setDepthError,
      saveSiteData,
      saveFieldName: 'maxDepth',
      saveWithTimeout: updateMaximumDepthValuesTimeout
    })
  }

  const handleSystemsChange = async (selectedValues) => {
    setSelectedSystems(selectedValues)
    await saveSiteData({
      terrestrial_system: selectedValues.includes(1),
      marine_system: selectedValues.includes(2),
      freshwater_system: selectedValues.includes(3),
      subterranean_system: selectedValues.includes(4)
    })
  }

  const handleRationaleChange = async (e) => {
    setRationaleForSiteInformation(e.target.value)
    await updateRationaleForSiteInformationTimeout(() =>
      saveSiteData({ rationaleForSiteInformation: e.target.value })
    )
  }

  const handleDescriptionChange = async (e) => {
    setSiteDescription(e.target.value)
    await updateSiteDescriptionTimeout(() => saveSiteData({ siteDescription: e.target.value }))
  }

  const handleSensitiveSiteToggle = async () => {
    if (disabled) {
      return
    }
    sensitiveSiteDetailsDebounce.cancel()
    const newValue = !sensitiveSite
    setSensitiveSite(newValue)
    if (!newValue) {
      setSensitiveSiteDetails('')
    }

    await saveSiteData({
      sensitive_site: newValue,
      sensitive_site_details: newValue ? (sensitiveSiteDetails?.trim() || null) : null
    })

    if (refreshSiteCounts) refreshSiteCounts()
    if (refreshSites) refreshSites()
  }

  const handleSensitiveSiteDetailsChange = async (e) => {
    const value = e.target.value
    setSensitiveSiteDetails(value)

    await sensitiveSiteDetailsDebounce.schedule(() =>
      saveSiteData({
        sensitive_site: sensitiveSiteRef.current,
        sensitive_site_details: value?.trim() || null
      })
    )
  }

  const handleManagedChange = async (e) => {
    setHowIsTheSiteManaged(e.target.value)
    await updateHowIsTheSiteManagedTimeout(() => saveSiteData({ howIsTheSiteManaged: e.target.value }))
  }

  const handleSupersidedSitesChange = async (e) => {
    setSupersidedSites(e.target.value)
    await updateSupersededValuesTimeout(() => saveSiteData({ supersededSitesDesription: e.target.value }))
  }

  const handleDelineationChange = async (e) => {
    setDeliniationRationale(e.target.value)
    await updateDeliniationRationaleTimeout(() => saveSiteData({ deliniationRationale: e.target.value }))
  }

  const handleBoundarySourceChange = async (e) => {
    setBoundarySource(e.target.value)
    await updateBoundarySourceTimeout(() => saveSiteData({ boundary_source: e.target.value }))
  }

  const handleBiodiversityValuesChange = async (e) => {
    setAdditionalBiodiversityValues(e.target.value)
    await updateBiodiversityValuesTimeout(() => saveSiteData({ additionalBiodiversityValues: e.target.value }))
  }

  const handleLandUseChange = async (e) => {
    setLandUseRegimesAtSite(e.target.value)
    await updateLandUseRegimesAtSiteTimeout(() => saveSiteData({ landUseRegimesAtSite: e.target.value }))
  }

  const handleCoveredByPaChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: setCoveredByPa,
      saveSiteData,
      saveFieldName: 'calculatedProtectedArea',
      saveWithTimeout: updateCoveredByPaValuesTimeout
    })
  }

  const handleCoveredByOECMChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: setCoveredByOECM,
      saveSiteData,
      saveFieldName: 'calculatedOECMArea',
      saveWithTimeout: updateCoveredByOECMValuesTimeout
    })
  }

  const handleCoveredByWhatProtectedAreasChange = async (e) => {
    setCoveredByWhatProtectedAreas(e.target.value)
    await updateCoveredByWhatProtectedAreasTimeout(() =>
      saveSiteData({ coveredByWhatProtectedAreas: e.target.value })
    )
  }

  const handlePaUserUpdatedChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: setPaUserUpdated,
      saveSiteData,
      saveFieldName: 'pa_user_updated',
      saveWithTimeout: updatePaUserUpdatedTimeout
    })
  }

  const handlePaUserUpdatedJustificationChange = async (e) => {
    setPaUserUpdatedJustification(e.target.value)
    await updatePaUserUpdatedJustificationTimeout(() =>
      saveSiteData({ pa_user_updated_justification: e.target.value })
    )
  }

  const handleOecmUserUpdatedChange = async (e) => {
    await handleCalculatedCoverageChange({
      value: e.target.value,
      setValue: setOecmUserUpdated,
      saveSiteData,
      saveFieldName: 'oecm_user_updated',
      saveWithTimeout: updateOecmUserUpdatedTimeout
    })
  }

  const handleOecmUserUpdatedJustificationChange = async (e) => {
    setOecmUserUpdatedJustification(e.target.value)
    await updateOecmUserUpdatedJustificationTimeout(() =>
      saveSiteData({ oecm_user_updated_justification: e.target.value })
    )
  }

  const handleThreatsDescriptionChange = async (e) => {
    setThreatsDescription(e.target.value)
    await updateThreatsDescriptionValuesTimeout(() => saveSiteData({ threatsDescription: e.target.value }))
  }

  const handleNotesChange = async (e) => {
    setNotes(e.target.value)
    await updateNotesTimeout(() => saveSiteData({ notes: e.target.value }))
  }

  const handleReferencesChange = async (e) => {
    setReferences(e.target.value)
    await updateReferencessTimeout(() => saveSiteData({ references: e.target.value }))
  }

  const handleChangeContributors = async (e) => {
    setContributors(e.target.value)
    await contributorsSavetimeout(() => saveSiteData({ consultationWithContributors: e.target.value }))
  }

  const handleHabitatChange = async (e) => {
    setHabitat(e.target.value)
    await updateHabitatTimeout(() => saveSiteData({ habitatDescription: e.target.value }))
  }

  const showPaOecmCovered = hasProtectedOrConservedCoverage({
    coveredByPa,
    coveredByOECM,
    paUserUpdated,
    oecmUserUpdated
  })

  return (
    <Container>
      <AssessmentTitle
        titleId='nameAndLocation'
        subTitleId='nameAndLocationDescription'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-name-location-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Input
              value={siteData.siteCode}
              label='siteId'
              placeholder='noPlaceholder'
              infoTooltip='siteId'
              disabled={!canEditPaOecmOverrides}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Input
              value={siteNameNational}
              onChange={handleSiteNationalNameChange}
              label='siteNameNational'
              field='siteNameNational'
              placeholder='nationalSiteName'
              dataTest='form-field'
              infoTooltip='siteNameNationalHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.siteNameNational, 'siteNameNational')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Input
              value={name}
              onChange={handleNameChange}
              label='siteNameInternational'
              field='siteNameEnglish'
              placeholder='internationalSiteName'
              dataTest='form-field'
              infoTooltip='siteNameEnglishHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.name, 'siteNameEnglish')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <MultiSelectionDropdown
              marginTop='16px'
              label='countryLabel'
              field='siteDetailsCountry'
              secondStepOptions={countriesOptions}
              value={CountryCode}
              saveChanges={handleCountryChange}
              infoTooltip='countryHint'
              inputValue={countryInput}
              onInputChange={handleSearchCountry}
              isSearchable={true}
              selectAllOption={true}
              error={getRequiredFieldError(siteProposalFieldValidation.details.SiteDetailsCountryId, 'siteDetailsCountry')}
            />
          </Col>
          <Col md={4}>
            <Input
              value={state}
              onChange={handleStateChange}
              label='stateProvince'
              placeholder='stateProvincePlaceholder'
              dataTest='form-field'
              infoTooltip='stateProvinceHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='siteDetails'
        subTitleId='siteDetailsDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-details-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='siteDescription'
              field='siteDescription'
              value={siteDescription}
              onChange={handleDescriptionChange}
              placeholder='siteDescriptionPlaceholder'
              infoTooltip='siteDescriptionHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.siteDescription, 'siteDescription')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <div
              data-test='sensitive-site-section'
              style={{ width: '100%', marginBottom: '16px' }}
            >
              <Checkbox
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <FormattedMessage id='sensitiveSiteLabel' />
                    <Tooltip
                      style={{ zIndex: 1000 }}
                      zIndex='1070'
                      placement='bottom'
                      trigger={['hover']}
                      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                      overlay={{ label: 'sensitiveSiteLabel', infoTooltip: 'sensitiveSiteHint' }}
                      hyper='https://www.keybiodiversityareas.org'
                      textForLink='www.keybiodiversityareas.org'
                      type='big'
                    >
                      <AssessmentPageSubTittleIconWrapper>
                        <InfoIcon />
                      </AssessmentPageSubTittleIconWrapper>
                    </Tooltip>
                  </span>
                }
                checked={sensitiveSite}
                onChange={handleSensitiveSiteToggle}
                field='sensitiveSite'
                disabled={disabled}
              />
            </div>
            <Textarea
              height='128px'
              label='sensitiveSiteDetailsLabel'
              field='sensitiveSiteDetails'
              value={sensitiveSiteDetails}
              onChange={handleSensitiveSiteDetailsChange}
              placeholder='sensitiveSiteDetailsPlaceholder'
              infoTooltip='sensitiveSiteDetailsHint'
              disabled={disabled || !sensitiveSite}
              zIndex='1070'
              error={
                sensitiveSite &&
                siteProposalFieldValidation.details.sensitiveSiteDetails === false &&
                !String(sensitiveSiteDetails || '').trim()
                  ? {
                    field: 'sensitiveSiteDetails',
                    error: 'sensitiveSiteDetailsRequired'
                  }
                  : { field: '', error: '' }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='siteArea'
              field='siteArea'
              value={siteArea}
              onChange={handleAreaChange}
              placeholder='siteAreaPlaceholder'
              dataTest='form-field'
              infoTooltip='siteAreaHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
          <Col md={4}>
            <Input
              label='latitude'
              field='latitudeOfMidPoint'
              value={latitudeOfMidPoint}
              onChange={handleLatitudeChange}
              placeholder='latitudePlaceholder'
              dataTest='form-field'
              infoTooltip='latitudeOfCentroidHint'
              disabled={disabled}
              isOptional={true}
              type='number'
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='longitude'
              field='longitudeOfMidPoint'
              value={longitudeOfMidPoint}
              onChange={handleLongitudeChange}
              placeholder='longitudePlaceholder'
              dataTest='form-field'
              infoTooltip='longitudeOfCentroidHint'
              disabled={disabled}
              isOptional={true}
              type='number'
            />
          </Col>
          <Col md={4}>
            <Input
              label='lowestElevation'
              field='lowestAltitude'
              value={lowestAltitude}
              onChange={handleLowestAltitudeChange}
              placeholder='lowestElevationPlaceholder'
              dataTest='form-field'
              infoTooltip='lowestElevationHint'
              type='number'
              disabled={disabled}
              error={getAltitudeFieldError(siteProposalFieldValidation.details.lowestAltitude, altitudeError, 'lowestAltitude')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='highestElevation'
              field='highestAltitude'
              value={highestAltitude}
              onChange={handleHighestAltitudeChange}
              placeholder='highestElevationPlaceholder'
              dataTest='form-field'
              infoTooltip='highestElevationHint'
              type='number'
              disabled={disabled}
              error={getAltitudeFieldError(siteProposalFieldValidation.details.highestAltitude, altitudeError, 'highestAltitude')}
            />
          </Col>
          <Col md={4}>
            <Input
              label='minimumDepth'
              field='minimumDepth'
              value={minimumDepth}
              onChange={handleMinimumDepthChange}
              placeholder='minimumDepthPlaceholder'
              dataTest='form-field'
              infoTooltip='minimumDepthHint'
              disabled={disabled}
              isOptional={true}
              error={getDepthFieldError(depthError, 'minimumDepth')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='maximumDepth'
              field='maximumDepth'
              value={maximumDepth}
              onChange={handleMaximumDepthChange}
              placeholder='maximumDepthPlaceholder'
              dataTest='form-field'
              infoTooltip='maximumDepthHint'
              disabled={disabled}
              isOptional={true}
              error={getDepthFieldError(depthError, 'maximumDepth')}
            />
          </Col>
          <Col md={4}>
            <MultiSelectionDropdown
              marginTop='16px'
              label='siteSystemsLabel'
              field='siteSystems'
              secondStepOptions={ecoSystem}
              value={selectedSystems}
              saveChanges={handleSystemsChange}
              infoTooltip='siteSystemsHint'
              placeholder='siteSystemsPlaceholder'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='rationale'
              field='rationaleForSiteInformation'
              value={rationaleForSiteInformation}
              onChange={handleRationaleChange}
              placeholder='rationalePlaceholder'
              infoTooltip='rationaleHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.rationaleForSiteInformation, 'rationaleForSiteInformation')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='biodiversityValues'
              value={additionalBiodiversityValues}
              onChange={handleBiodiversityValuesChange}
              placeholder='biodiversityValuesPlaceholder'
              infoTooltip='biodiversityValueHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='siteValues'
              value={indigenousGroups}
              onChange={handleIndigenousGroupsChange}
              placeholder='siteValuesPlaceholder'
              infoTooltip='siteValueHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='manageabilityDelineation'
        subTitleId='manageabilityDelineationDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-manageability-delineation-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='manageability'
              field='howIsTheSiteManaged'
              value={howIsTheSiteManaged}
              onChange={handleManagedChange}
              placeholder='siteManagedPlaceholder'
              infoTooltip='siteManagedHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.howIsTheSiteManaged, 'howIsTheSiteManaged')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='supersidedSites'
              field='supersidedSites'
              value={supersidedSites}
              onChange={handleSupersidedSitesChange}
              placeholder='supersidedSitesPlaceholder'
              infoTooltip='supersidedSitesHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='delineation'
              field='deliniationRationale'
              value={deliniationRationale}
              onChange={handleDelineationChange}
              placeholder='delineationPlaceholder'
              infoTooltip='delineationHint'
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.deliniationRationale, 'deliniationRationale')}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='boundarySourceLabel'
              field='boundary_source'
              value={boundarySource}
              onChange={handleBoundarySourceChange}
              placeholder='boundarySourcePlaceholder'
              infoTooltip='boundarySourceHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='protectedConserved'
        subTitleId='protectedConservedDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-protected-conserved-areas-form'>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='coverageByPa'
              labelTitle='coveredByPa'
              field='coveredByPa'
              value={coveredByPa}
              onChange={handleCoveredByPaChange}
              placeholder='coveredByPaPlaceholder'
              dataTest='form-field'
              infoTooltip='coveredByPaHint'
              readOnly={true}
              hyper={'https://www.protectedplanet.net/en/thematic-areas/wdpa?tab=WDPA'}
            />
          </Col>
          <Col md={4}>
            <Input
              label='alternativePaEstimate'
              field='pa_user_updated'
              value={paUserUpdated}
              onChange={handlePaUserUpdatedChange}
              placeholder='alternativePaEstimatePlaceholder'
              dataTest='form-field'
              infoTooltip='alternativePaEstimateHint'
              readOnly={!canEditPaOecmOverrides}
              isOptional={true}
            />
          </Col>
        </Row>
        {hasAlternativeCoverageValue(paUserUpdated) && (
          <Row>
            <Col md={8} mdOffset={2}>
              <Textarea
                height='96px'
                label='alternativePaJustification'
                field='pa_user_updated_justification'
                value={paUserUpdatedJustification}
                onChange={handlePaUserUpdatedJustificationChange}
                placeholder='alternativePaJustificationPlaceholder'
                infoTooltip='alternativePaJustificationHint'
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label='coverageArea'
              labelTitle='coveredByOECM'
              field='coveredByOECM'
              value={coveredByOECM}
              onChange={handleCoveredByOECMChange}
              placeholder='coveredByOECMPlaceholder'
              dataTest='form-field'
              infoTooltip='coveredByOECMHint'
              readOnly={true}
              hyper={'https://www.protectedplanet.net/en/thematic-areas/wdpa?tab=WDPA'}
            />
          </Col>
          <Col md={4}>
            <Input
              label='alternativeOecmEstimate'
              field='oecm_user_updated'
              value={oecmUserUpdated}
              onChange={handleOecmUserUpdatedChange}
              placeholder='alternativeOecmEstimatePlaceholder'
              dataTest='form-field'
              infoTooltip='alternativeOecmEstimateHint'
              readOnly={!canEditPaOecmOverrides}
              isOptional={true}
            />
          </Col>
        </Row>
        {hasAlternativeCoverageValue(oecmUserUpdated) && (
          <Row>
            <Col md={8} mdOffset={2}>
              <Textarea
                height='96px'
                label='alternativeOecmJustification'
                field='oecm_user_updated_justification'
                value={oecmUserUpdatedJustification}
                onChange={handleOecmUserUpdatedJustificationChange}
                placeholder='alternativeOecmJustificationPlaceholder'
                infoTooltip='alternativeOecmJustificationHint'
              />
            </Col>
          </Row>
        )}
        {showPaOecmCovered && (
          <Row>
            <Col md={8} mdOffset={2}>
              <Textarea
                height='96px'
                label='pasAndOrOecmsCovered'
                field='coveredByWhatProtectedAreas'
                value={coveredByWhatProtectedAreas}
                onChange={handleCoveredByWhatProtectedAreasChange}
                placeholder='pasAndOrOecmsCoveredPlaceholder'
                infoTooltip='pasAndOrOecmsCoveredHint'
              />
            </Col>
          </Row>
        )}
      </div>
      <AssessmentTitle
        titleId='habitatsLand'
        subTitleId='habitatsLandDescription'
        marginTop='64px'
        marginBottom='56px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-habitats-land-use-form'>
        <Row>
          <Col md={11} mdOffset={-2}>
            <Habitats site={siteData} disabled={editOnlySiteStatus || disabled} />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='habitatDescription'
              field='habitatDescription'
              value={habitat}
              onChange={handleHabitatChange}
              placeholder='habitatPlaceholder'
              infoTooltip='habitatHint'
              isOptional={true}
              disabled={disabled}
              hyper={'https://www.iucnredlist.org/resources/habitat-classification-scheme'}
              textForLink={'IUCN Habitat Classification Scheme'}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='landUse'
              value={landUseRegimesAtSite}
              onChange={handleLandUseChange}
              placeholder='landUsePlaceholder'
              infoTooltip='landUseHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='pressures'
        subTitleId='pressuresDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-pressures-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='threatsDescription'
              field='threatsDescription'
              value={threatsDescription}
              onChange={handleThreatsDescriptionChange}
              placeholder='threatsDescriptionPlaceholder'
              infoTooltip='threatsDescriptionHint'
              disabled={disabled}
              isOptional={true}
            />
          </Col>
        </Row>
      </div>
      <AssessmentTitle
        titleId='additionalDetails'
        subTitleId='additionalDetailsDescription'
        marginTop='64px'
        fontSizePageSubtitle='14px'
      />
      <div data-test='site-additional-details-form'>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='notes'
              value={notes}
              onChange={handleNotesChange}
              placeholder='notesPlaceholder'
              infoTooltip='notesHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              height='96px'
              label='references'
              value={references}
              onChange={handleReferencesChange}
              placeholder='referencesPlaceholder'
              infoTooltip='referencesHint'
              isOptional={true}
              disabled={disabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="contributors"
              value={contributors}
              field="contributors"
              infoTooltip="contributorsHint"
              onChange={handleChangeContributors}
              placeholder="contributorsPlaceholder"
              height="128px"
              dataTest="contributors-text-area"
              isOptional={false}
              disabled={disabled}
              error={getRequiredFieldError(siteProposalFieldValidation.details.consultationWithContributors, 'contributors')}
            />
          </Col>
        </Row>
      </div>
      <ScrollToTop bottom="104px" />
    </Container>
  )
}

export default DetailsPage
